import RichText from './RichText'
import DocumentChart from './DocumentChart'
import { CALLOUT, CELL, CHIP, ROW_BG, ROW_STRIPE, pick } from './documentTones'

const PARAGRAPH = {
  body: 'text-[15px] leading-relaxed text-gray-800 max-w-prose',
  lede: 'text-lg leading-relaxed text-gray-600 max-w-prose',
  lead: 'text-lg leading-relaxed text-gray-800',
  muted: 'text-sm leading-relaxed text-gray-500',
  small: 'text-xs leading-relaxed text-gray-500 max-w-prose',
  why: 'text-sm leading-relaxed text-gray-600 border-l-2 border-gray-200 pl-3',
  chain: 'text-sm leading-relaxed text-gray-800 bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono',
}

export function Chip({ text, tone }) {
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold align-middle whitespace-nowrap print:whitespace-normal ${pick(CHIP, tone)}`}>
      {text}
    </span>
  )
}

function Heading({ block }) {
  const Tag = block.level === 4 ? 'h4' : 'h3'
  const size = block.level === 4 ? 'text-base' : 'text-lg'
  return (
    <div className="flex flex-wrap items-center gap-2 pt-2 break-after-avoid">
      <Tag className={`${size} font-semibold text-gray-900`}><RichText text={block.text} /></Tag>
      {block.badge && <Chip text={block.badge.text} tone={block.badge.tone} />}
    </div>
  )
}

function StatTiles({ block }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6 print:grid-cols-6">
      {block.items.map((item, i) => (
        <div key={`${item.label}-${i}`} className="rounded-lg border border-gray-200 bg-surface-container px-3 py-2 break-inside-avoid">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{item.label}</div>
          <div className="font-mono text-xl font-semibold text-gray-900 tabular-nums">{item.value}</div>
          {item.sub && <div className="text-xs text-gray-500">{item.sub}</div>}
        </div>
      ))}
    </div>
  )
}

function Cell({ cell, align, first, rowTone }) {
  const value = typeof cell === 'string' ? { text: cell } : cell
  const classes = [
    'px-3 py-2 align-top',
    align === 'right' ? 'text-right font-mono tabular-nums whitespace-nowrap print:whitespace-normal' : '',
    first ? `border-l-4 font-medium text-gray-900 ${pick(ROW_STRIPE, rowTone)}` : '',
    value.tone ? CELL[value.tone] ?? '' : '',
  ].join(' ')
  return (
    <td className={classes}>
      {value.chip ? <Chip text={value.text} tone={value.chip} /> : <RichText text={value.text} />}
    </td>
  )
}

function Table({ block }) {
  return (
    <div className="overflow-x-auto print:overflow-visible">
      <table className="min-w-full text-sm border-collapse">
        {block.caption && (
          <caption className="caption-bottom text-left text-xs text-gray-500 pt-2"><RichText text={block.caption} /></caption>
        )}
        <thead>
          <tr className="border-b-2 border-gray-300">
            {block.columns.map((col, i) => (
              <th key={`${col.label}-${i}`} scope="col"
                className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 ${col.align === 'right' ? 'text-right' : 'text-left'} ${i === 0 ? 'pl-4' : ''}`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, r) => (
            <tr key={r} className={`border-b border-gray-200 break-inside-avoid ${ROW_BG[row.tone] ?? ''}`}>
              {row.cells.map((cell, c) => (
                <Cell key={c} cell={cell} align={block.columns[c]?.align} first={c === 0} rowTone={row.tone} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function List({ block }) {
  if (block.variant === 'chips') {
    return (
      <ul className="flex flex-wrap gap-1.5">
        {block.items.map((item, i) => (
          <li key={i} className="rounded border border-gray-200 bg-surface-container px-2 py-0.5 font-mono text-xs text-gray-700">
            <RichText text={item} />
          </li>
        ))}
      </ul>
    )
  }
  const Tag = block.ordered ? 'ol' : 'ul'
  return (
    <Tag className={`${block.ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-1.5 text-[15px] leading-relaxed text-gray-800 max-w-prose`}>
      {block.items.map((item, i) => <li key={i}><RichText text={item} /></li>)}
    </Tag>
  )
}

function Callout({ block }) {
  return (
    <aside className={`rounded-r-lg border-l-4 px-4 py-3 space-y-2 break-inside-avoid-page ${pick(CALLOUT, block.tone)}`}>
      {block.title && <h4 className="text-base font-semibold text-gray-900"><RichText text={block.title} /></h4>}
      {block.blocks.map((inner, i) => <DocumentBlock key={i} block={inner} />)}
    </aside>
  )
}

function Timeline({ block }) {
  return (
    <ol className="relative border-l-2 border-gray-200 ml-2 space-y-5">
      {block.items.map((item, i) => (
        <li key={i} className="relative pl-6 break-inside-avoid">
          <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full border-2 border-surface-container bg-teal-600" aria-hidden="true" />
          <div className="text-[11px] font-semibold uppercase tracking-wider text-teal-700">{item.when}</div>
          <h4 className="text-base font-semibold text-gray-900"><RichText text={item.title} /></h4>
          <div className="mt-1 space-y-2">
            {(item.blocks ?? []).map((inner, j) => <DocumentBlock key={j} block={inner} />)}
          </div>
        </li>
      ))}
    </ol>
  )
}

function Sources({ block }) {
  return (
    <div className="space-y-2">
      {block.title && <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{block.title}</p>}
      <ol className="grid gap-x-6 gap-y-1 sm:grid-cols-2 list-decimal pl-5 text-sm">
        {block.items.map((source, i) => (
          <li key={i}>
            <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-teal-700 underline underline-offset-2">
              {source.label}
            </a>
            <span className="hidden print:block text-[10px] text-gray-500 break-all">{source.url}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}

// VH-228 phase 0: one block of a stored cycle document. Unknown types render nothing.
export function DocumentBlock({ block }) {
  switch (block?.type) {
    case 'heading':
      return <Heading block={block} />
    case 'paragraph':
      return <p className={PARAGRAPH[block.variant] ?? PARAGRAPH.body}><RichText text={block.text} /></p>
    case 'stat_tiles':
      return <StatTiles block={block} />
    case 'table':
      return <Table block={block} />
    case 'list':
      return <List block={block} />
    case 'callout':
      return <Callout block={block} />
    case 'timeline':
      return <Timeline block={block} />
    case 'quote':
      return (
        <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700">
          <RichText text={block.text} />
          {block.attribution && <footer className="mt-1 text-xs not-italic text-gray-500">— {block.attribution}</footer>}
        </blockquote>
      )
    case 'chart_series':
      return <DocumentChart block={block} />
    case 'sources':
      return <Sources block={block} />
    default:
      return null
  }
}

export default DocumentBlock
